import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from './config/msalConfig';
import AuthenticatedApp from './AuthenticatedApp';
import PublicPortal from './pages/PublicPortal';
import { setMsalInstance } from './auditFetch';

// Swish IQ coach mode (shared iPad) lives at /coach/... and never signs in
// with Microsoft; its own chunk loads only on that path.
const CoachModeApp = lazy(() => import('./swishiq/CoachModeApp'));
// Coach sign-in page for iPads: /coach-login, and the root of swishiq.snakepit.com.au.
const CoachLoginApp = lazy(() => import('./swishiq/CoachLoginApp'));
// The front door of swishiq.snakepit.com.au: Trials (coach sign-in) or Management (roster.snakepit.com.au).
const LandingApp = lazy(() => import('./swishiq/LandingApp'));

// If this window is the popup MSAL opened for sign-in, the OPENER is
// monitoring our window.location.hash for the auth code and will close us
// once it has been read. We must NOT render the React app or initialize a
// second MSAL instance here — doing so consumes the code and the opener
// never gets it, so the parent never logs in. Just sit blank.
const hashStr = window.location.hash || '';
const isMsalPopupReturn =
  !!window.opener &&
  window.opener !== window &&
  (hashStr.startsWith('#code=') ||
    hashStr.startsWith('#error=') ||
    hashStr.includes('client_info=') ||
    hashStr.includes('id_token=') ||
    hashStr.includes('access_token='));

if (!isMsalPopupReturn) {
  const root = ReactDOM.createRoot(document.getElementById('root'));

  // Check for public availability token. Prefer the URL fragment (which never
  // reaches the server, Referer headers, or HTTP logs). Accept the legacy
  // `?availability=` query parameter for already-sent emails, but immediately
  // rewrite the URL into the fragment form so the token stops leaking once
  // the user lands on the page.
  let availabilityToken = null;
  const FRAGMENT_PREFIX = '#availability=';
  if (hashStr.startsWith(FRAGMENT_PREFIX)) {
    availabilityToken = decodeURIComponent(hashStr.slice(FRAGMENT_PREFIX.length));
  } else {
    const urlParams = new URLSearchParams(window.location.search);
    availabilityToken = urlParams.get('availability');
    if (availabilityToken) {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('availability');
      cleanUrl.hash = `availability=${availabilityToken}`;
      window.history.replaceState({}, '', cleanUrl.toString());
    }
  }

  const isCoachMode = window.location.pathname === '/coach' || window.location.pathname.startsWith('/coach/');
  const isSwishHost = /^swish(iq|hq)\./.test(window.location.hostname.toLowerCase());
  const isLanding = isSwishHost && !isCoachMode && (window.location.pathname === '/' || window.location.pathname === '');
  const isCoachLogin = !isCoachMode && !isLanding && (window.location.pathname === '/coach-login' || isSwishHost);

  if (isLanding) {
    root.render(
      <React.StrictMode>
        <Suspense fallback={null}>
          <LandingApp />
        </Suspense>
      </React.StrictMode>
    );
  } else if (isCoachLogin) {
    root.render(
      <React.StrictMode>
        <Suspense fallback={null}>
          <CoachLoginApp />
        </Suspense>
      </React.StrictMode>
    );
  } else if (isCoachMode) {
    root.render(
      <React.StrictMode>
        <Suspense fallback={null}>
          <CoachModeApp />
        </Suspense>
      </React.StrictMode>
    );
  } else if (availabilityToken) {
    // Render public availability page WITHOUT authentication
    root.render(
      <React.StrictMode>
        <PublicPortal token={availabilityToken} />
      </React.StrictMode>
    );
  } else {
    // Normal authenticated app flow
    const msalInstance = new PublicClientApplication(msalConfig);
    // Share MSAL instance with auditFetch for token acquisition
    setMsalInstance(msalInstance);
    root.render(
      <React.StrictMode>
        <MsalProvider instance={msalInstance}>
          <AuthenticatedApp />
        </MsalProvider>
      </React.StrictMode>
    );
  }
}
